import { Card, CardContent } from "../../components/beui/SurfaceCard";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ShieldCheck, BadgeCheck, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { MobileSettingsAction } from "../../components/MobileSettingsControls";

/**
 * v3.9.66 (Blueprint Stage 6 / H — trust) — the member's own account standing:
 * a clear status badge, verification signals, and any warnings they've received,
 * so safety state is transparent (not admin-only).
 */
export function AccountStanding({ mobileSettings }: { mobileSettings: boolean }) {
  const { t } = useI18n();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.myStanding>> | null>(null);
  const [open, setOpen] = useState(false);
  const [standingFailed, setStandingFailed] = useState(false);
  useEffect(() => { api.myStanding().then(setData).catch(() => setStandingFailed(true)); }, []);
  if (!data) return <p className="settings-standing-status text-sm text-mut" role={standingFailed ? "alert" : "status"}>{t(standingFailed ? "settings.standingLoadFailed" : "settings.standingLoading")}</p>;

  const map: Record<string, { label: string; cls: string; sub: string }> = {
    trusted: { label: t("settings.trusted", "Trusted"), cls: "bg-brand-green/10 text-brand-greendark", sub: t("settings.trustedDescription", "Verified and in good standing. Thanks for keeping KontrataPH safe.") },
    good: { label: "Good standing", cls: "bg-brand-green/10 text-brand-greendark", sub: "No issues on your account. Verify your identity to earn a Trusted badge." },
    attention: { label: t("settings.needsAttention", "Needs attention"), cls: "bg-brand-gold/15 text-[#8a5a00]", sub: t("settings.warningsDescription", "You've received one or more warnings. Please review them below.") },
    suspended: { label: t("settings.suspended", "Suspended"), cls: "bg-brand-red/10 text-brand-red", sub: t("settings.suspendedDescription", "Your account is currently restricted. Contact support if you believe this is a mistake.") },
  };
  const s = map[data.standing] || map.good;
  const reasonLabels: Record<string, string> = { spam: "Spam", scam: "Suspected scam", abuse: "Abusive behavior", offensive: "Offensive content", offplatform: "Off-platform dealing", other: "Other" };

  return (
    <Card size="compact" className="mb-4"><CardContent>
      {mobileSettings ? <>
        <section className={`settings-standing-card settings-standing-${data.standing}`}>
          <strong><ShieldCheck aria-hidden="true" />{data.standing === "good" ? t("settings.goodStanding") : s.label}</strong>
          <p>{data.standing === "good" ? t(data.identityVerified ? "settings.noAccountIssues" : "settings.goodStandingDescription") : s.sub}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {data.identityVerified && <span className="chip bg-navy-50 text-navy-600"><BadgeCheck className="w-3 h-3" />Verified Identity</span>}
            {data.verified && data.verifiedKind === "company" && <span className="chip bg-navy-50 text-navy-600"><BadgeCheck className="w-3 h-3" />Verified Company</span>}
          </div>
        </section>
        <MobileSettingsAction href="/get-verified" title={t("settings.verification")} description={t("settings.verificationDescription")} />
      </> : <>
        <div className="settings-standing-heading flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-navy" />
          <h3 className="font-bold text-navy">{t("settings.accountStanding")}</h3>
          <span className={`chip ml-auto ${s.cls}`}>{s.label}</span>
        </div>
        <p className="text-sm text-mut">{s.sub}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {data.identityVerified && <span className="chip bg-navy-50 text-navy-600"><BadgeCheck className="w-3 h-3" />Verified Identity</span>}
          {data.verified && data.verifiedKind === "company" && <span className="chip bg-navy-50 text-navy-600"><BadgeCheck className="w-3 h-3" />Verified Company</span>}
        </div>
        <Link href="/get-verified" className="btn-red mt-3 w-fit">
          {t("settings.verification")} →
        </Link>
      </>}
      {data.warningCount > 0 && (
        <div className="mt-3 border-t border-line pt-3">
          <button onClick={() => setOpen((v) => !v)} className="text-sm font-semibold text-[#8a5a00] flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4" />{data.warningCount} warning{data.warningCount === 1 ? "" : "s"} on your account {open ? "▲" : "▼"}
          </button>
          {open && (
            <div className="space-y-2 mt-2">
              {data.warnings.map((w) => (
                <div key={w.id} className="rounded-lg border border-line p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-navy">{reasonLabels[w.reason] || w.reason}</span>
                    <span className="text-[11px] text-mut">{new Date(w.createdAt).toLocaleDateString("en-PH", { dateStyle: "medium" })}</span>
                  </div>
                  {w.note && <p className="text-sm text-mut mt-1">{w.note}</p>}
                </div>
              ))}
              <p className="text-[11px] text-mut">Think a warning was issued in error? <Link href="/support" className="text-brand-red font-semibold">{t("support.contact")}</Link> to appeal.</p>
            </div>
          )}
        </div>
      )}
    </CardContent></Card>
  );
}
